import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

interface UiState {
  sidebarOpen: boolean
  theme: Theme
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setTheme: (theme: Theme) => void
}

/**
 * Genuinely client-side UI state, and nothing else.
 *
 * Server data belongs in TanStack Query. Filter, sort and pagination state
 * belongs in URL search params, so a view can be linked and reloaded. What is
 * left — chrome the user has toggled — lives here.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: 'system',

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },
    }),
    { name: 'multaqa.admin.ui' },
  ),
)

/**
 * Reflect the theme onto `<html class="dark">`, which is what the `dark:`
 * variant in @multaqa/tokens keys off.
 */
export function applyTheme(theme: Theme): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark)

  document.documentElement.classList.toggle('dark', isDark)
}
